/**
 * Rollback service — wraps the five rollback API endpoints.
 */

import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';
import { getEnv } from '../../config/env';
import type { Migration } from '../../types/migration';
import type { RollbackPlanResponse, RollbackQuickPreview } from '../../types/rollback';

// ---------------------------------------------------------------------------
// getRollbackQuickPreview
// ---------------------------------------------------------------------------

/**
 * Fetches a lightweight preview of what a rollback would affect.
 * GET /api/migrations/{id}/rollback-preview
 */
export async function getRollbackQuickPreview(
  token: string,
  migrationId: string
): Promise<RollbackQuickPreview> {
  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${encodeURIComponent(migrationId)}/rollback-preview`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: getV2AuthHeaders(token)
    });

    if (!response.ok) {
      throw new Error(
        `Error fetching rollback preview: ${response.status} ${response.statusText}`
      );
    }

    const data: RollbackQuickPreview = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching rollback preview:', error);
    throw error;
  }
}

// ---------------------------------------------------------------------------
// startRollback
// ---------------------------------------------------------------------------

/**
 * Triggers the rollback workflow for a migration (queues job4RollbackSetup).
 * POST /api/migrations/{id}/rollback
 * Returns the updated Migration record.
 */
export async function startRollback(token: string, migrationId: string): Promise<Migration> {
  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${encodeURIComponent(migrationId)}/rollback`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        ...getV2AuthHeaders(token),
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Error starting rollback: ${response.status} ${response.statusText}`);
    }

    const data: Migration = await response.json();
    return data;
  } catch (error) {
    console.error('Error starting rollback:', error);
    throw error;
  }
}

// ---------------------------------------------------------------------------
// getRollbackStatus
// ---------------------------------------------------------------------------

/**
 * Polls the current rollback status for a migration.
 * GET /api/migrations/{id}/rollback-status
 * Returns the updated Migration record (caller reads `.status` from it).
 */
export async function getRollbackStatus(token: string, migrationId: string): Promise<Migration> {
  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${encodeURIComponent(migrationId)}/rollback-status`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: getV2AuthHeaders(token)
    });

    if (!response.ok) {
      throw new Error(
        `Error fetching rollback status: ${response.status} ${response.statusText}`
      );
    }

    const data: Migration = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching rollback status:', error);
    throw error;
  }
}

// ---------------------------------------------------------------------------
// getRollbackPlan
// ---------------------------------------------------------------------------

/**
 * Retrieves the full rollback plan (all resources and their actions).
 * GET /api/migrations/{id}/rollback-plan
 */
export async function getRollbackPlan(
  token: string,
  migrationId: string
): Promise<RollbackPlanResponse> {
  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${encodeURIComponent(migrationId)}/rollback-plan`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: getV2AuthHeaders(token)
    });

    if (!response.ok) {
      throw new Error(`Error fetching rollback plan: ${response.status} ${response.statusText}`);
    }

    const data: RollbackPlanResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching rollback plan:', error);
    throw error;
  }
}

// ---------------------------------------------------------------------------
// approveRollback
// ---------------------------------------------------------------------------

/**
 * Approves the rollback plan, triggering job5RollbackApply.
 * POST /api/migrations/{id}/rollback-approve
 * Returns the updated Migration record.
 */
export async function approveRollback(token: string, migrationId: string): Promise<Migration> {
  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${encodeURIComponent(migrationId)}/rollback-approve`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        ...getV2AuthHeaders(token),
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Error approving rollback: ${response.status} ${response.statusText}`);
    }

    const data: Migration = await response.json();
    return data;
  } catch (error) {
    console.error('Error approving rollback:', error);
    throw error;
  }
}

// ---------------------------------------------------------------------------
// reviewRollbackPlan
// ---------------------------------------------------------------------------

/**
 * Records a reviewer's decision on the plan's "needs-review" resources and regenerates the plan.
 * The approved GUIDs are promoted into the destroy set on the next RollbackSetup run.
 * POST /api/migrations/{id}/rollback-plan/review
 */
export async function reviewRollbackPlan(
  token: string,
  migrationId: string,
  approvedForDeletion: string[]
): Promise<void> {
  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${encodeURIComponent(migrationId)}/rollback-plan/review`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        ...getV2AuthHeaders(token),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ approvedForDeletion, kept: [] })
    });

    if (!response.ok) {
      throw new Error(`Error submitting rollback review: ${response.status} ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error submitting rollback review:', error);
    throw error;
  }
}
